import {Directive, effect, ElementRef, HostListener, inject, Input, input} from '@angular/core';
import {ConnectionPositionPair, Overlay, OverlayPositionBuilder, OverlayRef} from '@angular/cdk/overlay';
import {TooltipComponent} from './tooltip.component';
import {ComponentPortal} from '@angular/cdk/portal';

@Directive({
  selector: '[libTooltip]',
  standalone: true,
})
export class TooltipDirective {

  private overlay = inject(Overlay);
  private elementRef = inject(ElementRef);
  private positionBuilder = inject(OverlayPositionBuilder);

  private overlayRef: OverlayRef | null = null;
  private longPressTimeout: ReturnType<typeof setTimeout> | null = null;

  // tooltipText = input<string>('');
  @Input() libTooltip = '';
  tooltipPosition = input<'top' | 'bottom' | 'left' | 'right' | 'before' | 'after'>('bottom');
  tooltipClass = input<string | string[] | Set<string> | { [klass: string]: any }>({alias: 'tooltipClass'});
  tooltipDisabled = input<boolean>(false);

  constructor() {
    effect(() => {
      this.hideTooltip();
    });
  }

  private showTooltip() {
    if (this.tooltipDisabled()) {
      return;
    }

    if (this.overlayRef?.hasAttached()) {
      return;
    }

    const position = this.getPosition(this.tooltipPosition());
    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([position]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const tooltipPortal = new ComponentPortal(TooltipComponent);
    const tooltipRef = this.overlayRef.attach(tooltipPortal);
    tooltipRef.instance.text = this.libTooltip;
    tooltipRef.instance.tooltipClass = this.tooltipClass();
  }

  private hideTooltip() {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  private getPosition(position: string): ConnectionPositionPair {
    switch (position) {
      case 'bottom':
        return {
          originX: 'center',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top',
          offsetY: 8,
        };
      case 'left':
      case 'before':
        return {
          originX: 'start',
          originY: 'center',
          overlayX: 'end',
          overlayY: 'center',
          offsetX: -8,
        };
      case 'right':
      case 'after':
        return {
          originX: 'end',
          originY: 'center',
          overlayX: 'start',
          overlayY: 'center',
          offsetX: 8,
        };
      default: // 'top'
        return {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetY: -8,
        };
    }
  }

  private isTouch(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  // 👉 Hover for desktop
  @HostListener('mouseenter')
  onMouseEnter() {
    if (!this.isTouch()) {
      this.showTooltip();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if (!this.isTouch()) {
      this.hideTooltip();
    }
  }

  // 👉 Long press for mobile
  @HostListener('touchstart')
  onTouchStart() {
    if (this.isTouch()) {
      this.longPressTimeout = setTimeout(() => {
        this.showTooltip();
      }, 500);
    }
  }

  @HostListener('touchend')
  @HostListener('touchcancel')
  onTouchEnd() {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
      this.longPressTimeout = null;
    }
    this.hideTooltip();
  }

}
